import { useState, useEffect, useMemo } from 'react';
import { Plus, TrendingDown, Edit, Trash2, Calendar, Wrench, ShoppingCart, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cajaChicaApi } from '../lib/api';
import LibroCaja from '../components/LibroCaja';

interface Movimiento {
  id: string;
  fecha: string;
  tipo: 'ingreso' | 'gasto' | 'saldo_inicial';
  monto: number;
  descripcion: string;
  categoria?: string;
  metodo_pago?: 'efectivo' | 'yape' | null;
}

type TipoCaja = 'mantenimiento' | 'ventas' | 'servicios';

interface CajaCentralProps {
  caja: TipoCaja;
}

// Cada caja (Mantenimiento / Ventas / Servicios) es solo INGRESOS de lo que le
// corresponde a esa caja: Mantenimiento solo recibe el pago obligatorio de
// mantenimiento por bus; Ventas recibe las ventas de productos y la parte de
// productos de los pagos de credito; Servicios recibe la parte de mano de
// obra/servicio manual de ventas, ventas retroactivas y pagos de credito -- separada
// de productos y de mantenimientos a proposito. Los egresos/gastos se manejan aparte,
// en la pantalla de Gastos, donde se elige de que caja sale cada gasto -- aca no se
// registran ni se muestran gastos, para que esto quede limpio y enfocado solo en lo
// que entra a esta caja puntual. El "Saldo Actual" si resta los gastos que se le
// asignaron a esta caja, porque es el efectivo real disponible de esta caja.
const TITULOS: Record<TipoCaja, string> = {
  mantenimiento: 'Caja de Mantenimiento',
  ventas: 'Caja de Repuestos',
  servicios: 'Caja de Servicios',
};
const ICONOS: Record<TipoCaja, typeof Wrench> = {
  mantenimiento: Wrench,
  ventas: ShoppingCart,
  servicios: Sparkles,
};
const DESCRIPCIONES: Record<TipoCaja, string> = {
  mantenimiento: 'Solo el pago obligatorio de mantenimiento de cada bus. Los gastos se registran aparte, en "Gastos", eligiendo de qué caja salen.',
  ventas: 'Desde el 10/10/2026 une Caja Ventas y Caja Chica. Entran las ventas de repuestos y la parte de repuestos de los pagos de crédito; salen las compras de repuestos y los gastos. Empezó con S/ 2,415.50.',
  servicios: 'Cobros de servicios (mano de obra, independiente de productos y mantenimientos): de ventas, ventas antiguas y pagos de crédito de buses. Los gastos se registran aparte, en "Gastos", eligiendo de qué caja salen.',
};
const CajaCentral = ({ caja }: CajaCentralProps) => {
  const navigate = useNavigate();
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [saldo, setSaldo] = useState(0);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [cargando, setCargando] = useState(true);
  const mesActual = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const [mesSeleccionado, setMesSeleccionado] = useState(mesActual());
  const [movimientoEditando, setMovimientoEditando] = useState<Movimiento | null>(null);
  const [nuevoMovimiento, setNuevoMovimiento] = useState({ monto: '', descripcion: '', clave: '' });
  // Caja Repuestos abre en el Libro de caja (formato del Excel de Rogelio, 2026-10-20)
  const [vista, setVista] = useState<'libro' | 'ingresos'>('libro');
  const verLibro = caja === 'ventas' && vista === 'libro';

  const titulo = TITULOS[caja];
  const Icono = ICONOS[caja];
  const descripcionPantalla = DESCRIPCIONES[caja];

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caja]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const dataMovimientos = await cajaChicaApi.getAll(caja);
      // Solo ingresos (y el saldo inicial, que no es un movimiento del dia a dia): los
      // gastos se ven y se manejan desde la pantalla de Gastos.
      setMovimientos((dataMovimientos || []).filter((m: Movimiento) => m.tipo !== 'gasto'));

      const dataSaldo = await cajaChicaApi.getSaldo(caja);
      setSaldo(dataSaldo.saldo || 0);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setCargando(false);
    }
  };

  const mesesDisponibles = useMemo(() => {
    const meses = new Set(movimientos.map(m => m.fecha.substring(0, 7)));
    meses.add(mesActual());
    return Array.from(meses).sort().reverse();
  }, [movimientos]);
  const formatearMes = (mesStr: string) => {
    const [ano, mes] = mesStr.split('-');
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${meses[parseInt(mes) - 1]} ${ano}`;
  };

  const movimientosFiltrados = movimientos.filter(m => m.fecha.substring(0, 7) === mesSeleccionado);
  const ingresosMes = movimientosFiltrados.filter(m => m.tipo === 'ingreso').reduce((sum, m) => sum + m.monto, 0);
  // Efectivo real en caja vs. Yape (va directo a una cuenta bancaria, no es plata física
  // que se pueda contar en la caja) -- separados para que el arqueo de caja cuadre.
  const ingresosEfectivoMes = movimientosFiltrados
    .filter(m => m.tipo === 'ingreso' && m.metodo_pago !== 'yape')
    .reduce((sum, m) => sum + m.monto, 0);
  const ingresosYapeMes = movimientosFiltrados
    .filter(m => m.tipo === 'ingreso' && m.metodo_pago === 'yape')
    .reduce((sum, m) => sum + m.monto, 0);

  const abrirModal = () => {
    setMostrarModal(true);
    setMovimientoEditando(null);
    setNuevoMovimiento({ monto: '', descripcion: '', clave: '' });
  };

  const abrirEdicion = (movimiento: Movimiento) => {
    if (movimiento.tipo === 'saldo_inicial') {
      alert('No se puede editar el saldo inicial');
      return;
    }
    setMovimientoEditando(movimiento);
    setMostrarModal(true);
  };

  const guardarMovimiento = async () => {
    if (movimientoEditando) {
      if (!movimientoEditando.monto || !movimientoEditando.descripcion) return alert('Complete los campos');
      try {
        await cajaChicaApi.update(movimientoEditando.id, {
          tipo: movimientoEditando.tipo,
          monto: movimientoEditando.monto,
          descripcion: movimientoEditando.descripcion
        });
        setMostrarModal(false);
        setMovimientoEditando(null);
        cargarDatos();
      } catch (error) {
        console.error('Error guardando:', error);
        alert('Error al guardar');
      }
      return;
    }

    if (!nuevoMovimiento.monto || !nuevoMovimiento.descripcion) return alert('Complete los campos');
    if (nuevoMovimiento.clave !== '2020') return alert('Clave incorrecta');

    try {
      await cajaChicaApi.create({
        tipo: 'ingreso',
        monto: parseFloat(nuevoMovimiento.monto),
        descripcion: nuevoMovimiento.descripcion,
        clave: nuevoMovimiento.clave,
        caja,
      });
      setMostrarModal(false);
      cargarDatos();
    } catch (error: any) {
      console.error('Error guardando:', error);
      alert(error.message || 'Error al guardar');
    }
  };

  const eliminarMovimiento = async (id: string, tipo: string) => {
    if (tipo === 'saldo_inicial') {
      alert('No se puede eliminar el saldo inicial');
      return;
    }
    if (!confirm('¿Eliminar? Queda registrado en Auditoría.')) return;
    await cajaChicaApi.delete(id);
    cargarDatos();
  };

  const getTipoLabel = (tipo: string) => tipo === 'saldo_inicial' ? 'Saldo Inicial' : 'Ingreso';
  const getTipoColor = (tipo: string) => tipo === 'saldo_inicial' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Icono className="text-blue-600" size={32} />
            {titulo}
          </h1>
          <div className="flex flex-wrap gap-2">
          {caja === 'ventas' && (
            <button
              onClick={() => navigate('/compras-repuestos')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold"
              title="Registrar compras de repuestos (salen de esta caja)"
            >
              <Wrench size={16} />
              Compras de repuestos
            </button>
          )}
          {(caja === 'ventas' || caja === 'servicios') && (
            <button
              onClick={() => navigate('/creditos')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"
              title="Registrar el pago que hace un cliente (propietario) por todas sus deudas, repartido entre sus buses"
            >
              <Plus size={16} />
              Cobrar deuda de un cliente
            </button>
          )}
          <button
            onClick={() => navigate('/gastos')}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            <TrendingDown size={16} className="text-red-600" />
            Ver Gastos
          </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 -mt-4 mb-6">{descripcionPantalla}</p>

        <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl shadow-lg p-6 mb-6 text-white">
          <p className="text-blue-100 text-sm uppercase mb-1">Saldo Actual{caja === 'ventas' ? ' (efectivo)' : ''}</p>
          <p className="text-4xl font-bold">S/ {saldo.toFixed(2)}</p>
          <p className="text-blue-100 text-xs mt-1">Ya descuenta los gastos asignados a esta caja</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 max-w-3xl">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-600">Ingresos ({mesSeleccionado})</p>
            <p className="text-2xl font-bold text-green-600">S/ {ingresosMes.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-400">
            <p className="text-sm text-gray-600">Efectivo</p>
            <p className="text-2xl font-bold text-gray-700">S/ {ingresosEfectivoMes.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
            <p className="text-sm text-gray-600">Yape</p>
            <p className="text-2xl font-bold text-purple-600">S/ {ingresosYapeMes.toFixed(2)}</p>
          </div>
        </div>

        {caja === 'ventas' && (
          <div className="flex gap-2 mb-4">
            <button onClick={() => setVista('libro')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${vista === 'libro' ? 'bg-blue-600 text-white' : 'bg-white border hover:bg-gray-50'}`}>
              Libro de caja (entradas, salidas y saldo)
            </button>
            <button onClick={() => setVista('ingresos')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${vista === 'ingresos' ? 'bg-blue-600 text-white' : 'bg-white border hover:bg-gray-50'}`}>
              Historial de ingresos
            </button>
          </div>
        )}
        {verLibro && <LibroCaja onCambio={cargarDatos} />}

        {!verLibro && <>
        <button onClick={abrirModal} className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-center gap-2 mb-6 w-full md:w-auto">
          <Plus size={24} />
          Registrar Ingreso Manual
        </button>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Calendar className="text-blue-600" />
            Historial de Ingresos
          </h2>

          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm font-medium">Mes:</label>
            <select value={mesSeleccionado} onChange={(e) => setMesSeleccionado(e.target.value)} className="border rounded px-3 py-2">
              {mesesDisponibles.map(mes => (
                <option key={mes} value={mes}>{formatearMes(mes)}</option>
              ))}
            </select>
            <span className="text-sm text-gray-600">{movimientosFiltrados.length} registros</span>
          </div>

          {cargando ? (
            <p className="text-center py-8 text-gray-500">Cargando...</p>
          ) : movimientosFiltrados.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay movimientos</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {movimientosFiltrados.map((mov) => (
                    <tr key={mov.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">{format(new Date(mov.fecha), 'dd/MM/yyyy HH:mm')}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTipoColor(mov.tipo)}`}>
                          {getTipoLabel(mov.tipo)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {mov.tipo === 'ingreso' && (
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${mov.metodo_pago === 'yape' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                            {mov.metodo_pago === 'yape' ? 'YAPE' : 'EFECTIVO'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">{mov.descripcion}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                        + S/ {mov.monto.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {mov.tipo !== 'saldo_inicial' && (
                          <>
                            <button onClick={() => abrirEdicion(mov)} className="text-blue-600 hover:text-blue-800 mr-3">
                              <Edit size={18} />
                            </button>
                            <button onClick={() => eliminarMovimiento(mov.id, mov.tipo)} className="text-red-600 hover:text-red-800">
                              <Trash2 size={18} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>}
      </div>

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold mb-4">
              {movimientoEditando ? 'Editar Ingreso' : `Registrar Ingreso — ${titulo}`}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Monto (S/)</label>
                <input
                  type="number"
                  step="0.01"
                  value={movimientoEditando ? movimientoEditando.monto : nuevoMovimiento.monto}
                  onChange={(e) => movimientoEditando
                    ? setMovimientoEditando({...movimientoEditando, monto: parseFloat(e.target.value) || 0})
                    : setNuevoMovimiento({...nuevoMovimiento, monto: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descripción</label>
                <textarea
                  value={movimientoEditando ? movimientoEditando.descripcion : nuevoMovimiento.descripcion}
                  onChange={(e) => movimientoEditando
                    ? setMovimientoEditando({...movimientoEditando, descripcion: e.target.value})
                    : setNuevoMovimiento({...nuevoMovimiento, descripcion: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows={3}
                />
              </div>

              {!movimientoEditando && (
                <div>
                  <label className="block text-sm font-medium mb-2">Clave</label>
                  <input
                    type="password"
                    value={nuevoMovimiento.clave}
                    onChange={(e) => setNuevoMovimiento({...nuevoMovimiento, clave: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Clave de administrador"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setMostrarModal(false); setMovimientoEditando(null); }}
                className="flex-1 px-4 py-2 bg-gray-200 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={guardarMovimiento}
                className="flex-1 px-4 py-2 text-white rounded-lg bg-blue-600 hover:bg-blue-700"
              >
                {movimientoEditando ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CajaCentral;
